/**
 * PromotionCard component - Instagram-style card for displaying promotions
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Divider
} from '@mui/material';
import { 
  Schedule as TimeIcon
} from '@mui/icons-material';

import colors from '../../../themes/colors';
import { promotionTypography } from '../../../themes/typography';
import PromotionBadge from './PromotionBadge';
import SelectionCode from './SelectionCode';

/**
 * Price display component with before/after comparison
 */
const PriceDisplay = ({ subtitle, discountPercentage }) => {
  // Try to extract price information from subtitle with optional discount percentage
  const priceMatch = subtitle?.match(/Was\s*€?(\d+(?:\.\d+)?)\s*-\s*Now\s*€?(\d+(?:\.\d+)?)\s*(?:\(-(\d+)%\))?/i);
  
  if (priceMatch) {
    const [, originalPrice, newPrice, discountPercent] = priceMatch;
    return (
      <Box sx={{ textAlign: 'center', my: 2 }}>
        <Typography
          variant="body2"
          sx={{
            color: colors.text.secondary,
            fontSize: { xs: '0.875rem', md: '1rem' },
            mb: 0.5
          }}
        >
          Precio anterior:
        </Typography>
        <Typography
          variant="h6"
          sx={{
            ...promotionTypography.priceDisplay,
            color: colors.text.secondary,
            textDecoration: 'line-through',
            fontSize: { xs: '1rem', md: '1.125rem' }
          }}
        >
          €{originalPrice}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: colors.promotion.combo,
            fontSize: { xs: '0.875rem', md: '1rem' },
            fontWeight: 600,
            mt: 1,
            mb: 0.5
          }}
        >
          Precio especial:
        </Typography>
        <Typography
          variant="h4"
          sx={{
            ...promotionTypography.discountLarge,
            color: colors.promotion.combo,
            fontWeight: 700
          }}
        >
          €{newPrice}
        </Typography>
        {discountPercent && (
          <Typography
            variant="body1"
            sx={{
              color: colors.promotion.combo,
              fontSize: { xs: '1rem', md: '1.125rem' },
              fontWeight: 600,
              mt: 1
            }}
          >
            ¡{discountPercent}% de descuento!
          </Typography>
        )}
      </Box>
    );
  }

  // Fallback to just showing the discount
  return (
    <Box sx={{ textAlign: 'center', my: 2 }}>
      <Typography
        variant="h4"
        sx={{
          ...promotionTypography.discountLarge,
          color: colors.promotion.combo,
          fontWeight: 700
        }}
      >
        {subtitle || `${discountPercentage} OFF`}
      </Typography>
    </Box>
  );
};

/**
 * Promotion validity indicator
 */
const ValidityIndicator = ({ validFrom, validTo }) => {
  const now = new Date();
  const endDate = new Date(validTo);
  const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
  
  if (daysLeft <= 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        mt: 2,
        px: 2,
        py: 1,
        bgcolor: daysLeft <= 3 ? colors.status.warning + '20' : colors.ui.hover,
        borderRadius: 2,
        border: daysLeft <= 3 ? `1px solid ${colors.status.warning}` : 'none'
      }}
    >
      <TimeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      <Typography
        variant="caption"
        sx={{
          color: daysLeft <= 3 ? colors.status.warning : 'text.secondary',
          fontWeight: daysLeft <= 3 ? 600 : 400,
          fontSize: { xs: '0.75rem', md: '0.875rem' }
        }}
      >
        {daysLeft === 1 ? '¡Expira hoy!' : `${daysLeft} días restantes`}
      </Typography>
    </Box>
  );
};

/**
 * Main promotion card component
 */
const PromotionCard = ({ promotion, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  const {
    type,
    cleanTitle,
    subtitle,
    selectionCode,
    discountPercentage,
    validFrom,
    validTo,
    machineName
  } = promotion;


  return (
    <Card
      sx={{
        mx: { xs: 1, md: 2 },
        mb: 3,
        minHeight: { xs: 'auto', md: 'auto' },
        borderRadius: { xs: 3, md: 4 },
        background: colors.promotion.cardBg,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${colors.ui.border}`,
        boxShadow: isHovered
          ? `0 20px 40px ${colors.promotion.shadow}`
          : `0 8px 32px ${colors.promotion.shadow}`,
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        position: 'relative',
        overflow: 'clip',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: type === 'combo' 
            ? colors.promotion.combo 
            : colors.promotion.discount,
          zIndex: 1
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent
        sx={{
          p: { xs: 3, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          textAlign: 'center',
          position: 'relative',
          minHeight: { xs: 280, md: 320 }
        }}
      >
        {/* Top Section */}
        <Box>
          {/* Promotion Badge */}
          <PromotionBadge type={type} />

          {/* Title */}
          <Typography
            variant="h4"
            sx={{
              ...promotionTypography.heroTitle,
              mb: 2,
              color: colors.text.primary,
              lineHeight: 1.2,
              wordBreak: 'break-word'
            }}
          >
            {cleanTitle}
          </Typography>

          {/* Price/Discount Display */}
          <PriceDisplay 
            subtitle={subtitle} 
            discountPercentage={discountPercentage}
          />

          {/* Divider */}
          <Divider 
            sx={{ 
              my: 3, 
              bgcolor: colors.ui.divider,
              opacity: 0.5
            }} 
          />
        </Box>

        {/* Middle Section - Selection Code */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: '100%' }}>
            <SelectionCode
              code={selectionCode}
            />
          </Box>
        </Box>

        {/* Bottom Section */}
        <Box>
          {/* Validity Indicator */}
          {validTo && (
            <ValidityIndicator 
              validFrom={validFrom} 
              validTo={validTo} 
            />
          )}

        </Box>

        {/* Background Decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${
              type === 'combo' ? colors.promotion.combo : colors.promotion.discount
            }10 0%, transparent 70%)`,
            opacity: 0.5,
            zIndex: 0
          }}
        />
      </CardContent>
    </Card>
  );
};

export default PromotionCard;